/**
 * Shared type definitions used across all EngineX modules.
 */

// ─── Loop Phases ────────────────────────────────────────────────────────────────

export const enum LoopPhase {
  Input = 0,
  FixedSim = 1,
  Animation = 2,
  RenderSub = 3,
  Render = 4,
  Post = 5,
  UI = 6,
}

export const enum SubPhase {
  PreUpdate = 0,
  Update = 1,
  PostUpdate = 2,
}

// ─── Component Types ────────────────────────────────────────────────────────────

export const enum ComponentType {
  Boolean,
  I8, I16, I32,
  U8, U16, U32,
  F32, F64,
  Vector2, Vector3, Vector4,
  Quaternion, Matrix4,
  String, Entity, Object,
}

// ─── Event Map ──────────────────────────────────────────────────────────────────

export interface EngineEventMap {
  'entity:created': { entity: number };
  'entity:destroyed': { entity: number };
  'scene:loaded': { name: string };
  'scene:unloaded': { name: string };
  'physics:collision-start': { entityA: number; entityB: number };
  'physics:collision-end': { entityA: number; entityB: number };
  'physics:trigger-enter': { entity: number; trigger: number };
  'physics:trigger-exit': { entity: number; trigger: number };
  'physics:contact-force': { entityA: number; entityB: number; totalForce: { x: number; y: number; z: number }; totalForceMagnitude: number };
  // Contact event bus (physics3d/ContactEvents.ts). A = the tracked collider; entity −1 when a collider has no entity.
  /** A pair's first contact-force crossing; `normalSpeed` is the PRE-impact approach speed (m/s), `force` in N. */
  'physics:impact': { colliderA: number; colliderB: number; entityA: number; entityB: number; materialA: string; materialB: string; force: number; impulse: number; normalSpeed: number; tangentSpeed: number; point: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } };
  /** Sustained sliding contact (rate-limited per pair); `tangentSpeed` from velocityAtPoint at the contact (m/s). */
  'physics:scrape': { colliderA: number; colliderB: number; entityA: number; entityB: number; materialA: string; materialB: string; tangentSpeed: number; normalForce: number; point: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } };
  /** The pair stopped touching (or a collider went away); `duration` in seconds. */
  'physics:contact-end': { colliderA: number; colliderB: number; entityA: number; entityB: number; materialA: string; materialB: string; duration: number };
  'net:connected': { identity: string };
  'net:disconnected': { reason: string };
  'net:entity-spawned': { serverId: bigint; localId: number };
  'net:entity-despawned': { localId: number };

  // XR
  'xr:session-started': { mode: string; binding: 'webgl' | 'webgpu'; supportsLayers: boolean; depthRange: 'neg1to1' | '0to1' };
  'xr:session-ended': Record<string, never>;
  'xr:visibility-change': { state: string };
  'xr:controller-connected': { index: number; handedness: string; hasHand: boolean };
  'xr:controller-disconnected': { index: number };
  'xr:hit-test-result': { hasResult: boolean };
  'xr:anchor-created': { anchorId: number };
  'xr:anchor-lost': { anchorId: number };
  'xr:plane-added': { planeId: number; orientation: 'horizontal' | 'vertical'; semanticLabel: string };
  'xr:plane-updated': { planeId: number; orientation: 'horizontal' | 'vertical'; semanticLabel: string };
  'xr:plane-removed': { planeId: number };
  'xr:mesh-added': { meshId: number; vertexCount: number; semanticLabel: string };
  'xr:mesh-updated': { meshId: number; vertexCount: number; semanticLabel: string };
  'xr:mesh-removed': { meshId: number };

  // AI
  'ai:sound': { pos: { x: number; y: number; z: number }; loudness: number; tag?: string; instigator?: number; t: number };
  'ai:squad-contact': { squadId: string; entity: number; targetEntityId: number; position: { x: number; y: number; z: number }; confidence: number };
  'ai:llm-response': { entity: number; intentKind: string; dialogue: string[] };

  // Rollback Netcode
  'rollback:performed': { startTick: number; depth: number; resimFrames: number };
  'rollback:desync': { tick: number; localHash: string; remoteHash: string };
  'rollback:hard-resync': { tick: number; reason: string };

  // AOI / Interest Management
  'aoi:cell-changed': { prevCell: { cx: number; cy: number; cz: number }; newCell: { cx: number; cy: number; cz: number } };
  'aoi:subscription-swapped': { queryCount: number; swapCount: number };
  'aoi:subscription-error': { error: string };

  // SpacetimeDB 2.0 Event Table Bridge
  'stdb:event': { tableName: string; row: Record<string, unknown> };

  // VirtualScreen
  'virtualscreen:created': { entity: number };
  'virtualscreen:destroyed': { entity: number };
  'virtualscreen:pointer-enter': { entity: number; source: string };
  'virtualscreen:pointer-leave': { entity: number; source: string };
  'virtualscreen:click': { entity: number; x: number; y: number; source: string };

  // HTML ECS
  'html:entity-created': { entity: number; domRefId: string };
  'html:entity-removed': { entity: number; domRefId: string };
  'html:parse-error': { domRefId: string; message: string };
  'html:patch-applied': { patchCount: number };

  // Versona
  'versona:response': { entity: number; text: string };
  'versona:move': { entity: number; target: { x: number; y: number; z: number } };
  'versona:lua-execute': { script: string; entity: number };
  'versona:sound': { entity: number; name: string };
  'versona:interact': { entity: number; action: string };
  'versona:spawn': { entity: number; prefab: string };

  // Input
  'input:device-changed': { previous: number; current: number };
  'input:gamepad-connected': { index: number; id: string; type: number };
  'input:gamepad-disconnected': { index: number };
  'input:gesture-detected': { type: number; state: number; hand: number; confidence: number };

  // Weather
  'weather:thunder': { intensity: number; delay: number };
  'weather:changed': { from: string; to: string };

  // Large World / Origin Rebase
  'world:origin-rebased': {
    /** Shift applied to all entity positions (dx, dy, dz in meters) */
    shiftX: number;
    shiftY: number;
    shiftZ: number;
    /** New absolute FP64 world origin */
    newOriginX: number;
    newOriginY: number;
    newOriginZ: number;
    /** Number of entities whose positions were updated */
    entityCount: number;
  };

  // Destruction (Chaos)
  'destruction:cluster-split': { instanceId: string; prevComponentCount: number; newComponentCount: number };
  'destruction:bond-broken': { instanceId: string; brokenBonds: number[] };

  // Quest system
  'quest:available': { questId: string };
  'quest:started': { questId: string };
  'quest:completed': { questId: string };
  'quest:objective_progress': { questId: string; objectiveId: string; progress: number; count: number };

  // Inventory system
  'inventory:added': { entity: number; itemId: string; count: number };
  'inventory:removed': { entity: number; itemId: string; count: number };

  // Dialogue system
  'dialogue:started': { treeId: string; nodeId: string };
  'dialogue:node': { nodeId: string; text: string };
  'dialogue:choice': { node: string; choiceIndex: number; choiceText: string };
  'dialogue:ended': { treeId: string | null };

  // Cutscene system
  'cutscene:started': { name: string };
  'cutscene:dialogue': { speaker: string; text: string; duration?: number };
  'cutscene:audio': { url: string; bus?: string };
  'cutscene:fade': { opacity: number; duration?: number };
  'cutscene:camera-move': { position: { x: number; y: number; z: number }; lookAt: { x: number; y: number; z: number }; duration: number; easing?: string };
  'cutscene:animation': { target: string; clip: string };
  'cutscene:ended': { name: string };

  // XR perf
  'xr:perf:warning': { fps: number; level: string };

  // Accessibility
  /**
   * Fired whenever an accessibility setting changes. `key` is the top-level
   * settings key; `value` is its new value (for nested style changes the key
   * is 'subtitleStyle' and value is the full new style object).
   */
  'a11y:changed': { key: string; value: unknown };
  'i18n:locale-changed': { locale: string; previous: string | null };

  // Soft bodies (Rapier ≥ 0.21, physics3d/SoftBodies.ts): a tear or a cut changed a body's topology.
  'physics:soft-tear': {
    /** SoftBodyInstance.id */
    body: number;
    name: string;
    cause: 'strain' | 'tear' | 'cut';
    tornEdges: number;
    tornCells: number;
    /** Rapier soft bodies the tear split off (new parts of the instance). */
    newParts: number;
    topologyVersion: number;
    /** World position of the tear. */
    x: number; y: number; z: number;
  };
}
